import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/shared/services/api';
import { useAuthStore } from '@/shared/stores/auth.store';
import { isSuperAdmin } from '@/shared/lib/roles';
import { ROUTES } from '@/shared/constants/routes';
import { readConsent } from './useConsent';

export interface VisitBody {
  site: 'app';
  path: string;
  referrer?: string;
  language?: string;
  timeZone?: string;
}

/** What one page view sends: the page, where from, language and time zone. Nothing is stored on the device. */
export function visitBody(pathname: string, search: string): VisitBody {
  return {
    site: 'app',
    path: `${pathname}${search}`,
    referrer: document.referrer || undefined,
    language: navigator.language || undefined,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
  };
}

/**
 * Counts each page view for the admin Website tab.
 *
 * Everyone is counted anonymously (cookieless, see the API's VisitsService).
 * A signed-in view carries the user only when they accepted analytics in the
 * consent bar; otherwise it is counted like any visitor. Staff and the admin
 * console are never counted, and nothing is sent in development.
 */
export function useVisitTracker(): void {
  const { pathname, search } = useLocation();
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const staff = useAuthStore((s) => isSuperAdmin(s.user));
  // A sign-in or sign-out on the same page is not a second view of it.
  const counted = useRef<string | null>(null);

  useEffect(() => {
    // Wait for the session, so a signed-in view is not first counted as anonymous.
    if (!import.meta.env.PROD || isLoading || staff) return;
    if (pathname === ROUTES.ADMIN || pathname.startsWith(`${ROUTES.ADMIN}/`)) return;
    const page = `${pathname}${search}`;
    if (counted.current === page) return;
    counted.current = page;
    const named = isAuthenticated && readConsent()?.analytics === true;
    void api.post(named ? '/visits/me' : '/visits', visitBody(pathname, search)).catch(() => undefined);
  }, [pathname, search, isLoading, isAuthenticated, staff]);
}
